import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { Formik, Field, Form, ErrorMessage, FieldArray, useFormikContext, setNestedObjectValues } from "formik";
import * as Yup from "yup";
import axios from "utils/axios";
import { CREATE_VENDOR_UPDATE_LINK_URL, GET_VENDOR_TAXONOMY_URL, PUBLIC_VENDOR_UPDATE_URL, UPLOAD_VENDOR_DOCUMENTS_URL } from "config/services";
import { useDispatch } from "react-redux";
import CustomSelect from "views/Rfqs/Components/CustomSelect";
import { useSearchParams } from "react-router-dom";
import VendorTaxonomySearch from "./VendorTaxonomySearch";
import VendorMakeSelections from "./VendorMakeSelections";
import { makeSelectionMap, materialMakeSelections } from "services/vendorMakeService";
import VendorInvitationThankYou from "./VendorInvitationThankYou";
import PincodeAddressFields from "components/PincodeAddressFields";
import {
    DEFAULT_MAX_FILE_SIZE,
    findInvalidFile,
    findOversizedFile,
} from "utils/fileUploadUtils";

const gstRegex =
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
const mobileNumberRegex = /^[6-9]\d{9}$/;
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const allowedVendorDocumentExtensions = ["pdf", "doc", "docx", "xls", "xlsx", "csv", "txt", "jpg", "jpeg", "png"];
const allowedVendorDocumentMimeTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
    "text/plain",
    "image/jpeg",
    "image/png",
];
const vendorDocumentAccept = allowedVendorDocumentExtensions.map((ext) => `.${ext}`).join(",");
const maxVendorDocumentSize = DEFAULT_MAX_FILE_SIZE;

const normalizeVendorContactFields = (values) => ({
    ...values,
    gstNumber: String(values.gstNumber || "").trim().toUpperCase(),
    contactDetails: (values.contactDetails || []).map((contact) => ({
        ...contact,
        email: typeof contact.email === "string"
            ? contact.email.trim().toLowerCase()
            : contact.email,
    })),
});

const getVendorFormAddress = (location = {}) => ({
    address: location?.addressLine1 || "",
    area: location?.area || "",
    city: location?.city || "",
    division: location?.division || "",
    state: location?.state || "",
    pincode: String(location?.pinCode || ""),
});

const isUnchangedLegacyAddress = (values, locations = []) => {
    const savedAddress = getVendorFormAddress(locations?.[0]);
    const isIncomplete = ["area", "city", "state", "pincode"].some(
        (field) => !String(savedAddress[field]).trim()
    );
    return isIncomplete && Object.keys(savedAddress).every(
        (field) => String(values?.[field] || "").trim() === String(savedAddress[field]).trim()
    );
};

const buildVendorAddressValidation = (preserveLegacyAddress) => {
    const textField = preserveLegacyAddress
        ? Yup.string().trim().nullable()
        : Yup.string().trim().required("Required");
    return {
        area: textField,
        city: textField,
        state: textField,
        pincode: preserveLegacyAddress
            ? Yup.string().nullable()
            : Yup.string()
                .transform((value) => value === "" ? null : value)
                .nullable()
                .required("Required")
                .matches(/^[1-9][0-9]{5}$/, "Pincode must be exactly 6 digits")
                .length(6, "Pincode must be exactly 6 digits"),
    };
};

const getConfirmedVendorUpdate = (response, vendorId) => {
    const result = response?.data;
    if (result?.error === true) throw new Error(result.message || "Update failed");
    if (result?.error !== false || !vendorId || result.payload?.vendorId !== vendorId) {
        throw new Error("The vendor update was not confirmed. Please try again.");
    }
    return result;
};

const getFirstValidationErrorPath = (errors, prefix = "") => {
    for (const key of Object.keys(errors || {})) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (typeof errors[key] === "string") return path;
        const nestedPath = getFirstValidationErrorPath(errors[key], path);
        if (nestedPath) return nestedPath;
    }
    return "";
};

const uniqueArray = (message) => {
    return Yup.array().test("unique", message, (array) => {
        if (array) {
            const normalizedArray = array.map((item) => item.toLowerCase());
            const set = new Set(normalizedArray);
            return set.size === array.length;
        }
        return true;
    });
};

const isTaxonomySearchResultSelected = (result, values) => {
    if (!result?.vendorType || !result?.disciplineName) return false;

    if (result.subCategoryName) {
        return (values.subcategories || []).includes(
            getSubcategoryKey(
                result.vendorType,
                result.disciplineName,
                result.categoryName,
                result.subCategoryName,
            ),
        );
    }

    if (result.categoryName) {
        return (values.categories || []).includes(
            getCategoryKey(
                result.vendorType,
                result.disciplineName,
                result.categoryName,
            ),
        );
    }

    return (values[`subtype_${result.vendorType}`] || []).includes(
        result.disciplineName,
    );
};

const getCategoryKey = (type, disciplineName, categoryName) =>
    `${type} - ${disciplineName} - ${categoryName}`;

const getSubcategoryKey = (type, disciplineName, categoryName, subcategoryName) =>
    `${getCategoryKey(type, disciplineName, categoryName)} > ${subcategoryName}`;

const isCategoryMissingRequiredSubcategory = (
    categoryKey,
    selectedSubcategories = [],
    taxonomyAll = {},
) => {
    if (!categoryKey) return false;

    const [vendorType, disciplineName, categoryName] = categoryKey.split(" - ");
    const category = taxonomyAll?.[vendorType]?.disciplines
        ?.find((discipline) => discipline?.name === disciplineName)
        ?.categories
        ?.find((item) => item?.name === categoryName);

    if (!category?.subCategories?.length) return false;

    return !selectedSubcategories.some((subcategoryKey) =>
        subcategoryKey.startsWith(`${categoryKey} > `)
    );
};

const hasRequiredSubcategoriesSelected = (selectedCategories = [], selectedSubcategories = [], taxonomyAll = {}) => {
    if (!selectedCategories.length) return true;

    return selectedCategories.every((categoryKey) => (
        !isCategoryMissingRequiredSubcategory(
            categoryKey,
            selectedSubcategories,
            taxonomyAll,
        )
    ));
};

const getFirstCategoryMissingSubcategory = (
    selectedCategories = [],
    selectedSubcategories = [],
    taxonomyAll = {},
) => {
    return (
        selectedCategories.find((categoryKey) => {
            return isCategoryMissingRequiredSubcategory(
                categoryKey,
                selectedSubcategories,
                taxonomyAll,
            );
        }) || null
    );
};

const toggleCategorySelection = ({
    category,
    categoryKey,
    checked,
    disciplineName,
    setFieldValue,
    type,
    values,
}) => {
    const selectedCategories = values.categories || [];
    const selectedSubcategories = values.subcategories || [];
    const isCategorySelected = selectedCategories.includes(categoryKey);
    const shouldSelect =
        typeof checked === "boolean" ? checked : !isCategorySelected;

    if (!shouldSelect) {
        setFieldValue(
            "categories",
            selectedCategories.filter((v) => v !== categoryKey)
        );

        setFieldValue(
            "subcategories",
            selectedSubcategories.filter(
                (sc) => !sc.startsWith(`${type} - ${disciplineName} - ${category.name} >`)
            )
        );
        return;
    }

    if (isCategorySelected) return;

    setFieldValue("categories", [...selectedCategories, categoryKey]);
};


const buildVendorFormLocations = (values, existingLocations = []) => {
    const locations = (Array.isArray(existingLocations) ? existingLocations : []).map((location) => {
        const address = { ...location };
        delete address.cityNormalized;
        delete address.areaNormalized;
        return address;
    });
    // Keep incomplete legacy addresses intact when only other vendor details change.
    if (isUnchangedLegacyAddress(values, existingLocations)) return locations;
    const primaryLocation = { ...locations[0] };
    delete primaryLocation.district;
    return [
        {
            ...primaryLocation,
            addressLine1: values.address || "",
            area: values.area || "",
            pinCode: values.pincode ? Number(values.pincode) : undefined,
            city: values.city || "",
            division: values.division || "",
            state: values.state || "",
        },
        ...locations.slice(1),
    ];
};

const VendorInvitationForm = ({
    user,
    editVendorDetails,
    isEdit,
    isApprovalMode,
    approvalMode,
    onFormReady,
    isPopupView = false,
    submissionSource = "INVITATION",
    onSuccess,
    publicUpdateToken = "",
    publicEntityName = "",
}) => {

    const [params] = useSearchParams();

    const formikRef = useRef(null);
    const formApiRegisteredRef = useRef(false);
    const isFormInitializedRef = useRef(false);
    const categoryRefs = useRef({});
    const categoryHighlightTimerRef = useRef(null);
    const approveVendorHandlerRef = useRef(null);
    const rejectVendorHandlerRef = useRef(null);
    const saveChangesHandlerRef = useRef(null);


    const invitedByName = params.get("name");
    const invitedById = params.get("userId");
    const invitedEntity = params.get("entityId");
    const entityName = params.get("entityName");
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submissionComplete, setSubmissionComplete] = useState(false);
    const [documentsUploading, setDocumentsUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { enqueueSnackbar } = useSnackbar();

    const [openDeletePopup, setOpenDeletePopup] = useState(false);
    const [showSubmitConfirmPopup, setShowSubmitConfirmPopup] = useState(false);
    const [taxonomyAll, setTaxonomyAll] = useState({});
    const [taxonomyError, setTaxonomyError] = useState("");
    const [copyingUpdateLink, setCopyingUpdateLink] = useState(false);
    const [vendorUpdateLink, setVendorUpdateLink] = useState("");
    const [categorySearchTerm, setCategorySearchTerm] = useState("");
    const [highlightedCategoryRefKey, setHighlightedCategoryRefKey] = useState("");
    const [initialFormValues, setInitialFormValues] = useState({
        name: "",
        yearsInBusiness: "",
        companyType: "",
        gstNumber: "",
        panNumber: "",
        brands: [],
        makeIdsBySubcategory: {},
        machinaries: [],
        manpower: "",
        staff: "",
        clientNames: [],
        contactDetails: [{ name: "", mobile: "", email: "" }],  //ALWAYS DEFAULT
        address: "",
        area: "",
        city: "",
        division: "",
        state: "",
        pincode: "",
        type: [],
        categories: [],
        subcategories: [],
        attachments: [],
    });

    const userId = user?.userId;
    const userName = user?.name;
    const entityId = user?.userEntity?.entityId;

    // ---------- DYNAMIC RULES ----------
    const dynamicRules = {
        requiresBrands: ["Material_Supplier"],
        requiresMachineries: ["Contractor"],
    };

    // ---------- DYNAMIC SUBTYPE SCHEMA GENERATOR ----------
    function generateSubtypeSchemas(taxonomyAll) {
        const result = {};

        Object.keys(taxonomyAll || {}).forEach((type) => {
            const field = `subtype_${type}`;

            result[field] = Yup.array().when("type", {
                is: (types) => types && types.includes(type),
                then: (schema) =>
                    schema
                        .min(1, `Select at least one ${type} discipline`)
                        .required(`At least one ${type} discipline is required`),
                otherwise: (schema) => schema.optional(),
            });
        });

        return result;
    }

    // ---------- FINAL COMPLETE VALIDATION SCHEMA ----------
    function buildValidationSchema(taxonomyAll) {
        return Yup.lazy((values) => Yup.object().shape({
            // dynamic subtype fields (subtype_Contractor, subtype_Material, ...)
            ...generateSubtypeSchemas(taxonomyAll),

            // old fields + dynamic checks
            name: Yup.string().required("Required"),

            yearsInBusiness: Yup.number()
                .transform((value, originalValue) =>
                    originalValue === "" ? null : value
                )
                .nullable()
                .positive("Must be a valid year")
                .integer("Must be a valid year")
                .min(1900, "Year must be after 1900")
                .max(new Date().getFullYear(), "Year cannot be in the future"),

            companyType: Yup.string().nullable(),

            gstNumber: Yup.string().trim().uppercase().matches(gstRegex, {
                message: "Invalid GST number",
                excludeEmptyString: true,
            }),

            panNumber: Yup.string()
                .required("Required")
                .matches(panRegex, "Invalid PAN number"),

            // Vendor types
            type: uniqueArray("No duplicate types allowed")
                .of(Yup.string().required("Required"))
                .min(1, "Required"),

            // Categories required unless "Others"
            categories: Yup.array().when("type", {
                is: (types) => types && !types.includes("Others"),
                then: (schema) => schema.min(1, "Please select at least one category").required("Required"),
                otherwise: (schema) => schema.optional(),
            }),

            // Others → custom category text
            otherVendorType: Yup.string().when("type", {
                is: (types) => types && types.includes("Others"),
                then: (schema) => schema.required("Please enter material / service"),
                otherwise: (schema) => schema.optional(),
            }),

            subcategories: Yup.array().test(
                "required-subcategories",
                "Please select at least one subcategory for each selected category.",
                function (subcategories = []) {
                    return hasRequiredSubcategoriesSelected(
                        this.parent?.categories || [],
                        subcategories,
                        taxonomyAll,
                    );
                },
            ),

            //  Dynamic brands validation
            brands: Yup.array().when("type", (types, schema) => {
                const mustHaveBrands =
                    types && dynamicRules.requiresBrands.some((t) => types.includes(t));
                return mustHaveBrands
                    ? schema.min(1, "Brands are required").required("Required")
                    : schema.optional();
            }),

            //  Dynamic machineries validation
            machinaries: Yup.array().nullable(),

            manpower: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? null : value))
                .nullable()
                .integer("Manpower must be a whole number")
                .min(0, "Manpower cannot be negative"),

            staff: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? null : value))
                .nullable()
                .integer("Staff must be a whole number")
                .min(0, "Staff cannot be negative"),

            clientNames: Yup.array().of(Yup.string()).nullable(),

            contactDetails: Yup.array()
                .of(
                    Yup.object({
                        name: Yup.string()
                            .matches(/^[A-Za-z\s]*$/, "Name can only contain letters and spaces")
                            .nullable(),

                        mobile: Yup.string()
                            .matches(mobileNumberRegex, "Invalid mobile number")
                            .nullable(),

                        email: Yup.string().trim().email("Invalid email address").nullable(),
                    }).test(
                        "email-or-mobile-required",
                        "Either email or mobile is required",
                        function (value) {
                            return Boolean(value?.email || value?.mobile);
                        }
                    )
                )
                .required("Must have contact details")
                .min(1, "Minimum of 1 contact detail"),

            ...buildVendorAddressValidation(
                Boolean(isEdit && editVendorDetails?.vendorId) &&
                isUnchangedLegacyAddress(values, editVendorDetails?.location)
            ),
            addressLine: Yup.string().nullable(),
            division: Yup.string().max(80, "Division must be 80 characters or fewer").nullable(),

            attachments: Yup.array().of(
                Yup.object({
                    Name: Yup.string().required(),
                    Content: Yup.string().required(),
                    ContentType: Yup.string().nullable(),
                    FileSize: Yup.number().nullable(),
                })
            ),
        }));
    }

    const handleVendorDocumentUpload = async (event, values, setFieldValue) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";

        if (!files.length) return;

        const invalidFile = findInvalidFile(files, {
            allowedExtensions: allowedVendorDocumentExtensions,
            allowedMimeTypes: allowedVendorDocumentMimeTypes,
        });
        if (invalidFile) {
            enqueueSnackbar(`File type not allowed: ${invalidFile.name}`, { variant: "warning" });
            return;
        }

        const oversizedFile = findOversizedFile(files, maxVendorDocumentSize);
        if (oversizedFile) {
            enqueueSnackbar(`File must be under 10 MB: ${oversizedFile.name}`, { variant: "warning" });
            return;
        }

        try {
            setDocumentsUploading(true);
            const formData = new FormData();
            files.forEach((file) => {
                formData.append("documents", file);
            });

            const response = await axios.post(UPLOAD_VENDOR_DOCUMENTS_URL, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            const uploadedFiles = response?.data?.payload || [];

            setFieldValue("attachments", [
                ...(Array.isArray(values.attachments) ? values.attachments : []),
                ...uploadedFiles,
            ]);

            enqueueSnackbar("Documents uploaded successfully", { variant: "success" });
        } catch (error) {
            const message = error?.response?.data?.message || "Failed to upload documents";
            enqueueSnackbar(message, { variant: "error" });
        } finally {
            setDocumentsUploading(false);
        }
    };

    const removeVendorDocument = (index, values, setFieldValue) => {
        setFieldValue(
            "attachments",
            (values.attachments || []).filter((_, attachmentIndex) => attachmentIndex !== index)
        );
    };

    const handleDigitsOnlyInput = (event, setFieldValue, fieldName) => {
        setFieldValue(fieldName, event.target.value.replace(/\D/g, ""));
    };

    // Function to handle mobile number input - only allow digits
    const handleMobileInput = (e, setFieldValue, index) => {
        const value = e.target.value;
        const digitsOnly = value.replace(/\D/g, '');
        const limitedDigits = digitsOnly.slice(0, 10);
        setFieldValue(`contactDetails[${index}].mobile`, limitedDigits);
    };

    // Check if contractor type is selected
    const isContractorSelected = (types) => {
        return types && types.includes("Contractor");
    };

    const getCategoryRefKey = (
        type,
        disciplineName,
        categoryName
    ) => `${type}__${disciplineName}__${categoryName}`;

    // Check if Material_Supplier type is selected
    const isMaterialSupplierSelected = (types) => {
        return types && types.includes("Material");
    };

    const applyTaxonomySearchResult = ({
        checked = true,
        result,
        setFieldValue,
        values,
    }) => {
        if (!result?.vendorType || !result?.disciplineName) return;

        const nextTypes = values.type || [];
        const subtypeField = `subtype_${result.vendorType}`;
        const selectedDisciplines = values[subtypeField] || [];
        const selectedCategories = values.categories || [];
        const selectedSubcategories = values.subcategories || [];

        if (!checked) {
            if (result.subCategoryName) {
                const subcategoryKey = getSubcategoryKey(
                    result.vendorType,
                    result.disciplineName,
                    result.categoryName,
                    result.subCategoryName,
                );

                setFieldValue(
                    "subcategories",
                    selectedSubcategories.filter((key) => key !== subcategoryKey),
                );
                return;
            }

            if (result.categoryName) {
                const categoryKey = getCategoryKey(
                    result.vendorType,
                    result.disciplineName,
                    result.categoryName,
                );

                setFieldValue(
                    "categories",
                    selectedCategories.filter((key) => key !== categoryKey),
                );
                setFieldValue(
                    "subcategories",
                    selectedSubcategories.filter(
                        (key) => !key.startsWith(`${categoryKey} >`),
                    ),
                );
                return;
            }

            setFieldValue(
                subtypeField,
                selectedDisciplines.filter(
                    (disciplineName) => disciplineName !== result.disciplineName,
                ),
            );
            setFieldValue(
                "categories",
                selectedCategories.filter(
                    (key) =>
                        !key.startsWith(
                            `${result.vendorType} - ${result.disciplineName} -`,
                        ),
                ),
            );
            setFieldValue(
                "subcategories",
                selectedSubcategories.filter(
                    (key) =>
                        !key.startsWith(
                            `${result.vendorType} - ${result.disciplineName} -`,
                        ),
                ),
            );
            return;
        }

        if (!nextTypes.includes(result.vendorType)) {
            setFieldValue("type", [...nextTypes, result.vendorType]);
        }

        if (!selectedDisciplines.includes(result.disciplineName)) {
            setFieldValue(subtypeField, [
                ...selectedDisciplines,
                result.disciplineName,
            ]);
        }

        if (result.categoryName) {
            const categoryKey = getCategoryKey(
                result.vendorType,
                result.disciplineName,
                result.categoryName,
            );

            if (!selectedCategories.includes(categoryKey)) {
                setFieldValue("categories", [...selectedCategories, categoryKey]);
            }

            if (result.subCategoryName) {
                const subcategoryKey = getSubcategoryKey(
                    result.vendorType,
                    result.disciplineName,
                    result.categoryName,
                    result.subCategoryName,
                );

                if (!selectedSubcategories.includes(subcategoryKey)) {
                    setFieldValue("subcategories", [
                        ...selectedSubcategories,
                        subcategoryKey,
                    ]);
                }
            }
        }
        scrollToCategory(
            result.vendorType,
            result.disciplineName,
            result.categoryName
        );
    };

    const buildVendorTypeHierarchy = (values, taxonomyAll) => {
        const output = [];

        values.type.forEach((vendorType) => {

            // For "Others", save only custom value
            if (vendorType === "Others") {
                output.push({
                    vendorType: values.otherVendorType,
                    isCustom: true
                });
                return;
            }

            const mainTax =
                vendorType === "Contractor"
                    ? taxonomyAll.Contractor
                    : vendorType === "Material"
                        ? taxonomyAll.Material
                        : null;

            if (!mainTax) return;

            const entry = { vendorType, disciplines: [] };

            const selectedDisciplines = values[`subtype_${vendorType}`] || [];

            selectedDisciplines.forEach((discName) => {
                const discObj = mainTax.disciplines.find((d) => d.name === discName);
                if (!discObj) return;

                const disciplineNode = {
                    disciplineId: discObj._id,
                    categories: []
                };

                // Selected categories for this discipline
                const selectedCategories = (values.categories || [])
                    .filter(c => c.startsWith(`${vendorType} - ${discName} - `))
                    .map(c => c.split(" - ")[2]);

                selectedCategories.forEach((catName) => {
                    const catObj = discObj.categories.find((c) => c.name === catName);
                    if (!catObj) return;

                    const categoryNode = {
                        categoryId: catObj._id,
                        subCategories: []
                    };

                    // Selected subcategories for this category
                    const selectedSubCats = (values.subcategories || [])
                        .filter(sc => sc.startsWith(`${vendorType} - ${discName} - ${catName} > `))
                        .map(sc => sc.split(" > ")[1]);

                    selectedSubCats.forEach((subCatName) => {
                        const sub = catObj.subCategories.find((s) => s.name === subCatName);
                        if (sub) {
                            categoryNode.subCategories.push({
                                subCategoryId: sub._id,
                                ...(Object.prototype.hasOwnProperty.call(values.makeIdsBySubcategory || {}, sub._id)
                                    ? { makeIds: values.makeIdsBySubcategory[sub._id] } : {}),
                            });
                        }
                    });

                    disciplineNode.categories.push(categoryNode);
                });

                entry.disciplines.push(disciplineNode);
            });

            output.push(entry);
        });

        return output;
    };


    const handleSubmit = async (values, { resetForm }) => {

        const vendorTypeHierarchy = buildVendorTypeHierarchy(values, taxonomyAll);

        const formData = normalizeVendorContactFields({
            vendorId: editVendorDetails?.vendorId,   // <– important for update
            vendorName: values?.name,
            entityId: invitedEntity || entityId,
            gstNumber: values?.gstNumber,
            panNumber: values?.panNumber,
            yearsInBusiness: values?.yearsInBusiness,
            companyType: values?.companyType || "",
            brands: values?.brands,
            machinaries: values?.machinaries,
            manpower: values?.manpower === "" ? null : Number(values?.manpower),
            staff: values?.staff === "" ? null : Number(values?.staff),
            clientNames: Array.isArray(values?.clientNames)
                ? values.clientNames.join(", ")
                : values?.clientNames || "",
            contactDetails: values?.contactDetails,
            type: values?.type,
            vendorTypeHierarchy,
            location: buildVendorFormLocations(values, editVendorDetails?.location),
            addedBy: invitedByName || userName,
            createdBy: invitedById || userId,
            attachments: Array.isArray(values?.attachments) ? values.attachments : [],
        });

        if (submissionSource === "MANUAL") {
            formData.isApproved = true;
        }
        if (publicUpdateToken) {
            try {
                await axios.put(PUBLIC_VENDOR_UPDATE_URL, formData, {
                    headers: { "x-vendor-update-token": publicUpdateToken },
                });
                setSubmissionComplete(true);
                window.scrollTo({ top: 0, behavior: "smooth" });
                return;
            } catch (error) {
                enqueueSnackbar(error.response?.data?.message || "Unable to update your profile. Please try again.", { variant: "error" });
                throw error;
            }
        }
        // ------- NORMAL EDIT MODE PATH --------
        if (isEdit) {
            try {
                const response = await axios.put(
                    "/api/directory/update/vendor",
                    formData,
                    {
                        params: {
                            vendorId: editVendorDetails?.vendorId,
                            entityId: invitedEntity || entityId,
                        },
                    }
                );

                const result = getConfirmedVendorUpdate(response, editVendorDetails?.vendorId);
                if (!onFormReady) enqueueSnackbar("Vendor updated successfully", { variant: "success" });
                return result;
            } catch (err) {
                const msg = err?.response?.data?.message || err?.message || "Update failed";
                if (!onFormReady) enqueueSnackbar(msg, { variant: "error" });
                throw err;
            }
        }

        // ------- CREATE MODE PATH (DEFAULT / DO NOT TOUCH THIS) -------
        try {
            const response = await axios.post(
                "/api/directory/add/vendor/for-approval",
                formData
            );

            if (submissionSource === "INVITATION") {
                setSubmissionComplete(true);
            } else {
                enqueueSnackbar("Profile submitted for approval successfully", {
                    variant: "success"
                });
            }

            resetForm();
            setSelectedLocations([]);
            if (onSuccess) {
                onSuccess();
            }
            window.scrollTo({ top: 0, behavior: "smooth" });

        } catch (error) {
            const message =
                error?.response?.data?.message ||
                "Something went wrong while submitting vendor";

            enqueueSnackbar(message, { variant: "error" });
            throw error;
        }
    };

    const copyVendorUpdateLink = async () => {
        setCopyingUpdateLink(true);
        try {
            const response = await axios.post(CREATE_VENDOR_UPDATE_LINK_URL, {
                entityId: invitedEntity || entityId,
                vendorId: editVendorDetails?.vendorId,
            });
            const token = response.data?.payload?.token;
            if (!token) throw new Error("Missing update token");
            const link = new URL("/invite-vendor", window.location.origin);
            link.searchParams.set("updateToken", token);
            setVendorUpdateLink(link.href);
            try {
                if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
                await navigator.clipboard.writeText(link.href);
                enqueueSnackbar("Vendor update link copied. It is valid for 7 days while approval is pending.", { variant: "success" });
            } catch (_) {
                enqueueSnackbar("Your update link is ready. Select and copy the link shown below.", { variant: "info" });
            }
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Unable to create the update link. Please try again.", { variant: "error" });
        } finally {
            setCopyingUpdateLink(false);
        }
    };

    const handleOpenDeletePopup = () => setOpenDeletePopup(true);
    const handleCloseDeletePopup = () => setOpenDeletePopup(false);


    function mapVendorCategoriesToForm(vendorCategories, taxonomyAll) {
        const result = {
            type: [],
            categories: [],
            subcategories: [],
            subtypeFields: {}   // e.g. subtype_Material, subtype_Contractor
        };

        if (!vendorCategories || !taxonomyAll) return result;

        vendorCategories.forEach((typeEntry) => {
            const vendorType = typeEntry.vendorType;
            result.type.push(vendorType);

            const tax = taxonomyAll[vendorType];
            if (!tax) return;

            const subtypeField = `subtype_${vendorType}`;
            result.subtypeFields[subtypeField] = [];

            typeEntry.disciplines.forEach((disc) => {
                const disciplineObj = tax.disciplines.find(d => d._id === disc.disciplineId);
                if (!disciplineObj) return;

                // Add discipline name to subtype array
                result.subtypeFields[subtypeField].push(disciplineObj.name);

                disc.categories.forEach((cat) => {
                    const categoryObj = disciplineObj.categories.find(c => c._id === cat.categoryId);
                    if (!categoryObj) return;

                    // Add category to UI-friendly format
                    result.categories.push(
                        `${vendorType} - ${disciplineObj.name} - ${categoryObj.name}`
                    );

                    cat.subCategories.forEach((sub) => {
                        const subcatObj = categoryObj.subCategories.find(s => s._id === sub.subCategoryId);
                        if (!subcatObj) return;

                        // Add subcategory to UI-friendly format
                        result.subcategories.push(
                            `${vendorType} - ${disciplineObj.name} - ${categoryObj.name} > ${subcatObj.name}`
                        );
                    });
                });
            });
        });

        return result;
    }

    const deleteVendorHandler = useCallback(async () => {
        if (!editVendorDetails?.vendorId) {
            throw new Error("Vendor ID missing");
        }

        await axios.delete(
            "/api/directory/delete/vendor",
            {
                params: {
                    vendorId: editVendorDetails.vendorId,
                    entityId: invitedEntity || entityId,
                    userName: userName,
                    userId: userId
                }
            }
        );
    }, [
        editVendorDetails,
        invitedEntity,
        entityId,
        userName,
        userId
    ]);


    const rejectVendorHandler = useCallback(async () => {
        if (!editVendorDetails?.vendorId) {
            throw new Error("Vendor ID missing");
        }

        await axios.put("/api/directory/reject/vendor", {
            vendorId: editVendorDetails.vendorId,
            entityId: invitedEntity || entityId,
            isApproved: false,
            rejectedBy: {
                userId,
                name: userName,
                rejectedAt: new Date()
            }
        });
    }, [editVendorDetails, userId, userName]);


    /* eslint-disable no-use-before-define */
    useEffect(() => {
        if (!isEdit || !editVendorDetails) return;
        if (!taxonomyAll || !Object.keys(taxonomyAll).length) return;
        if (isFormInitializedRef.current) return;

        const mapped = mapVendorCategoriesToForm(
            editVendorDetails.vendorCategories,
            taxonomyAll
        );

        setInitialFormValues(normalizeVendorContactFields({
            name: editVendorDetails.vendorName || "",
            yearsInBusiness: editVendorDetails.establishedYear || "",
            companyType: editVendorDetails.companyType || "",
            gstNumber: editVendorDetails.gstNumber || "",
            panNumber: editVendorDetails.panNumber || "",
            brands: editVendorDetails.brands || [],
            makeIdsBySubcategory: makeSelectionMap(editVendorDetails.vendorCategories),
            machinaries: editVendorDetails.machinaries || [],
            manpower: editVendorDetails.manpower ?? "",
            staff: editVendorDetails.staff ?? "",
            clientNames: editVendorDetails.clients || [],
            contactDetails: editVendorDetails.contactDetails?.length
                ? editVendorDetails.contactDetails
                : [{ name: "", mobile: "", email: "" }],

            ...getVendorFormAddress(editVendorDetails?.location?.[0]),

            type: mapped.type,
            categories: mapped.categories,
            subcategories: mapped.subcategories,
            attachments: editVendorDetails.attachments || [],
            ...mapped.subtypeFields
        }));

        isFormInitializedRef.current = true;
    }, [isEdit, editVendorDetails, taxonomyAll]);



    useEffect(() => {
        if (!onFormReady) return;
        if (!formikRef.current) return;

        const api = {};

        // Manual Edit Mode (Popup Edit from TableComponent)
        if (isEdit && !isApprovalMode) {
            api.saveChanges = (...args) => saveChangesHandlerRef.current?.(...args);
            api.deleteVendor = deleteVendorHandler;
        }

        // Approval Mode (Old existing logic — untouched)
        if (isApprovalMode) {
            api.approveVendor =
                approvalMode === "PENDING" || approvalMode === "ARCHIVED"
                    ? (...args) => approveVendorHandlerRef.current?.(...args)
                    : null;

            api.rejectVendor =
                approvalMode === "PENDING"
                    ? (...args) => rejectVendorHandlerRef.current?.(...args)
                    : null;

            api.saveChanges =
                approvalMode === "APPROVED"
                    ? (...args) => approveVendorHandlerRef.current?.(...args)
                    : null;
        }

        onFormReady(api);

    }, [
        onFormReady,
        isEdit,
        isApprovalMode,
        approvalMode,
        deleteVendorHandler
    ]);



    useEffect(() => {
        if (isEdit) {
            setSelectedLocations(editVendorDetails?.serviceLocations || []);
        } else {
            setSelectedLocations([]);
        }
    }, [editVendorDetails]);

    useEffect(() => {
        async function loadTaxonomies() {
            try {
                const res = await axios.get(GET_VENDOR_TAXONOMY_URL);

                // result: { Material: {...}, Contractor: {...} }
                setTaxonomyAll(res.data.data);
                if (!res.data.data || !Object.keys(res.data.data).length) setTaxonomyError("Category options are unavailable. Please reload this page to try again.");

            } catch (err) {
                console.error("Failed loading taxonomy", err);
                setTaxonomyError("Unable to load category options. Please reload this page to try again.");
            }
        }

        loadTaxonomies();
    }, []);

    useEffect(() => {
        return () => {
            window.clearTimeout(categoryHighlightTimerRef.current);
        };
    }, []);


    const getPanFromGst = (gst) => {
        if (!gst || gst.length < 12) return "";
        return gst.substring(2, 12); // index 2 to 11
    };

    const scrollToCategory = useCallback((
        type,
        disciplineName,
        categoryName
    ) => {
        const key = getCategoryRefKey(
            type,
            disciplineName,
            categoryName
        );

        const scrollWhenReady = (attempt = 0) => {
            const node = categoryRefs.current[key];

            if (node) {
                node.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                setHighlightedCategoryRefKey(key);
                window.clearTimeout(categoryHighlightTimerRef.current);
                categoryHighlightTimerRef.current = window.setTimeout(() => {
                    setHighlightedCategoryRefKey("");
                }, 2200);
                return;
            }

            if (attempt < 8) {
                setTimeout(() => scrollWhenReady(attempt + 1), 80);
            }
        };

        setTimeout(scrollWhenReady, 80);
    }, []);

    const markFormTouched = useCallback((formik) => {
        formik.setTouched(setNestedObjectValues(formik.values, true), false);
    }, []);

    const focusFirstValidationError = useCallback((errors = {}, values = {}) => {
        const firstErrorField = getFirstValidationErrorPath(errors);
        if (!firstErrorField) return;

        if (firstErrorField === "subcategories") {
            const categoryKey = getFirstCategoryMissingSubcategory(
                values.categories || [],
                values.subcategories || [],
                taxonomyAll,
            );

            if (categoryKey) {
                const [vendorType, disciplineName, ...categoryParts] = categoryKey.split(" - ");
                scrollToCategory(vendorType, disciplineName, categoryParts.join(" - "));
                return;
            }
        }

        const errorElement =
            document.getElementsByName(firstErrorField)[0] ||
            document.getElementById(firstErrorField);

        if (errorElement) {
            errorElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            errorElement.focus?.();
        }
    }, [scrollToCategory, taxonomyAll]);

    const saveChangesHandler = useCallback(async () => {
        if (documentsUploading) throw new Error("Please wait until document upload completes.");
        const formik = formikRef.current;
        if (!formik) throw new Error("Form not ready");
        if (formik.isSubmitting) throw new Error("A vendor update is already in progress.");

        const result = await formik.submitForm();
        if (!result) {
            const errors = await formik.validateForm();
            markFormTouched(formik);
            focusFirstValidationError(errors, formik.values);
            throw new Error("Please correct the highlighted fields before saving.");
        }
        return result;
    }, [documentsUploading, markFormTouched, focusFirstValidationError]);

    const approveVendorHandler = useCallback(async () => {
        if (documentsUploading) {
            enqueueSnackbar("Please wait until document upload completes", { variant: "warning" });
            throw new Error("Documents upload in progress");
        }

        const formik = formikRef.current;
        if (!formik) throw new Error("Form not ready");

        const shouldValidateBeforeSave =
            !isApprovalMode ||
            approvalMode === "PENDING" ||
            approvalMode === "APPROVED";

        if (shouldValidateBeforeSave) {
            const errors = await formik.validateForm();
            if (Object.keys(errors).length > 0) {
                markFormTouched(formik);
                focusFirstValidationError(errors, formik.values);
                throw new Error("Please correct the highlighted fields before saving.");
            }
        }

        // Keep archived approval behavior unchanged while enforcing validation for editable flows.
        const response = await axios.put("/api/directory/approve/vendor", {
            vendorId: editVendorDetails?.vendorId,
            entityId: invitedEntity || entityId,
            ...normalizeVendorContactFields(formik.values),
            vendorTypeHierarchy: buildVendorTypeHierarchy(formik.values, taxonomyAll),
            location: buildVendorFormLocations(formik.values, editVendorDetails?.location),
            isApproved: true,
            approvalMode: approvalMode,
            approvedBy: {
                userId,
                name: userName,
                approvedAt: new Date()
            }
        });
        return getConfirmedVendorUpdate(response, editVendorDetails?.vendorId);
    }, [
        approvalMode,
        editVendorDetails,
        taxonomyAll,
        invitedEntity,
        entityId,
        userId,
        userName,
        documentsUploading,
        enqueueSnackbar,
        isApprovalMode,
        markFormTouched,
        focusFirstValidationError,
    ]);

    useEffect(() => {
        approveVendorHandlerRef.current = approveVendorHandler;
        rejectVendorHandlerRef.current = rejectVendorHandler;
        saveChangesHandlerRef.current = saveChangesHandler;
    }, [approveVendorHandler, rejectVendorHandler, saveChangesHandler]);
    /* eslint-enable no-use-before-define */

    const getChipLabel = (chip) => {
        if (chip.type === "Sub Category") {
            return chip.value.split(" > ").pop();
        }

        if (chip.type === "Category") {
            return chip.value.split(" - ").pop();
        }

        return chip.value;
    };


    if (submissionComplete) {
        return <VendorInvitationThankYou isUpdate={Boolean(publicUpdateToken)} />;
    }
    if (publicUpdateToken && !isFormInitializedRef.current) {
        return <div role={taxonomyError ? "alert" : "status"} className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#FFF8F2] px-6 text-center text-sm text-[#8F430F]">
            {!taxonomyError && <span className="h-6 w-6 animate-spin rounded-full border-2 border-[#FFD9BE] border-t-[#FD6600]" />}
            <p>{taxonomyError || "Preparing your saved vendor profile..."}</p>
            {taxonomyError && <button type="button" onClick={() => window.location.reload()} className="rounded-lg bg-[#FD6600] px-4 py-2 font-semibold text-white">Try again</button>}
        </div>;
    }

    return (
        <div className="min-h-full text-sm">
            {/* Sticky Header */}
            {!isPopupView && (!isEdit || publicUpdateToken) && <div className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 px-6 py-4 shadow-md backdrop-blur">
                <div className="flex justify-between gap-x-2">
                    <div>
                        <h1 className="text-sm lg:text-base font-semibold">
                            {publicUpdateToken ? `Update Vendor Profile for ${publicEntityName}` : `Vendor Registration Form of ${entityName}`}
                            {user?.entityName && (
                                <span className="text-gray-600 font-normal ml-2">
                                    of {user.entityName}
                                </span>
                            )}<br />
                            <span className="text-[8px] lg:text-[10px] font-normal text-gray-600 ml-1">
                                (Powered by Neevay)
                            </span>
                        </h1>
                    </div>

                    {/* <div className="bg-orange-200 h-8 w-32">Neevay logo</div> */}

                    <img
                        src="/logos/Neevay_Logo.png"
                        alt='Neevay'
                        className='w-[60px] lg:w-[80px] h-full'
                    />
                </div>
            </div>}

            <Formik
                innerRef={formikRef}
                enableReinitialize
                initialValues={initialFormValues}
                validationSchema={buildValidationSchema(taxonomyAll)}
                onSubmit={handleSubmit}
                validateOnBlur={true}
                validateOnChange={false}
                validateOnMount={false}
            >
                {({ values, errors, submitForm, setFieldValue, validateForm, setTouched, touched }) => {
                    const hasVendorTypeSelections = (values.type || []).length > 0;
                    const clearVendorTypeSelections = () => {
                        setFieldValue("type", []);
                        Object.keys(taxonomyAll || {}).forEach((type) => {
                            setFieldValue(`subtype_${type}`, []);
                        });
                        setFieldValue("categories", []);
                        setFieldValue("subcategories", []);
                        setFieldValue("otherVendorType", "");
                        setCategorySearchTerm("");
                    };

                    const vendorTypeValidation = (() => {
                        if (!values.type?.length && errors.type) {
                            return {
                                id: "type",
                                message: "Please select at least one vendor type.",
                            };
                        }

                        if (
                            values.type?.length > 0 &&
                            !values.type.includes("Others") &&
                            !(values.categories || []).length &&
                            errors.categories
                        ) {
                            return {
                                id: "categories",
                                message: errors.categories,
                            };
                        }

                        if (errors.subcategories) {
                            return {
                                id: "subcategories",
                                message: errors.subcategories,
                            };
                        }

                        return null;
                    })();

                    return (
                        <>
                            <Form className="vendor-invitation-shell min-h-full bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF]">
                                <style>
                                    {`
                                        .vendor-invitation-content h2 {
                                            display: flex;
                                            align-items: center;
                                            gap: 0.5rem;
                                            color: #1f2937;
                                            font-size: 0.95rem;
                                            font-weight: 700;
                                            letter-spacing: -0.01em;
                                            margin-bottom: 0.75rem;
                                        }

                                        .vendor-invitation-content h2::before {
                                            content: "";
                                            width: 0.35rem;
                                            height: 1.3rem;
                                            border-radius: 999px;
                                            background: linear-gradient(180deg, #ff7a30 0%, #1d4ed8 100%);
                                            flex: 0 0 auto;
                                        }

                                        .vendor-invitation-content label {
                                            color: #4f4f42;
                                        }

                                        .vendor-invitation-content input:not([type="checkbox"]):not([type="radio"]):not([type="file"]):not(.border-0),
                                        .vendor-invitation-content textarea,
                                        .vendor-invitation-content select {
                                            border-color: #dcdcd0;
                                            min-height: 38px;
                                            box-shadow: none;
                                        }

                                        .vendor-invitation-content input:not([type="checkbox"]):not([type="radio"]):not([type="file"]):not(.border-0):focus,
                                        .vendor-invitation-content textarea:focus,
                                        .vendor-invitation-content select:focus {
                                            border-color: #1d4ed8;
                                            box-shadow: 0 0 0 1px rgba(29, 78, 216, 0.12);
                                        }

                                        .vendor-invitation-content hr {
                                            border-color: #e8e6dd;
                                            margin: 1.25rem 0;
                                        }

                                        .vendor-invitation-content .bg-gray-50 {
                                            background-color: #fcfcfa;
                                        }

                                        .vendor-invitation-content .shadow-sm {
                                            box-shadow: 0 8px 22px rgba(31, 30, 41, 0.04);
                                        }
                                    `}
                                </style>
                                <div className="vendor-invitation-content w-full space-y-5 px-4 py-5 pb-24 sm:px-6 lg:px-8">
                                    {isEdit && !publicUpdateToken && editVendorDetails?.vendorId && editVendorDetails.isApproved !== true && !editVendorDetails.isArchived && !editVendorDetails.isDeleted && (
                                        <section className="rounded-2xl border border-[#FFD8B8] bg-[#FFF8F2] p-4">
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <div>
                                                    <h2 className="text-sm font-semibold text-gray-900">Ask the vendor to update their profile</h2>
                                                    <p className="mt-1 text-xs text-gray-600">Share this private link only with this vendor. It expires after 7 days and stops working once approved.</p>
                                                </div>
                                                <button type="button" onClick={copyVendorUpdateLink} disabled={copyingUpdateLink} aria-busy={copyingUpdateLink} className="inline-flex items-center gap-2 rounded-lg bg-[#FD6600] px-4 py-2 text-sm font-semibold text-white disabled:cursor-wait disabled:opacity-70">
                                                    {copyingUpdateLink && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                                                    {copyingUpdateLink ? "Preparing link..." : "Copy update link"}
                                                </button>
                                            </div>
                                            {vendorUpdateLink && <input type="text" readOnly value={vendorUpdateLink} onFocus={(event) => event.currentTarget.select()} aria-label="Vendor update link" className="mt-3 w-full rounded-lg border border-[#EBD6C6] bg-white px-3 py-2 text-xs text-gray-700" />}
                                        </section>
                                    )}
                                    <section className="rounded-2xl border border-[#E8E6DD] bg-white/95 p-5 shadow-sm">
                                        <h2 className="text-lg font-semibold pt-2">Basic Details</h2>
                                        <div className="mt-2">
                                        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-2">
                                            <div className="lg:col-span-2">
                                                <label className="block text-sm font-medium">
                                                    Company Name<span className="text-red-500">*</span>
                                                </label>
                                                <Field
                                                    type="text"
                                                    name="name"
                                                    id="name"
                                                    className="mt-1 focus:ring-0 text-sm rounded-md block w-full border border-gray-300"
                                                />
                                                {!values.name && <div className="mt-1 text-xs text-red-500">Required</div>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium">
                                                    Year of Establishment
                                                </label>
                                                <div className="gap-2">
                                                    <input
                                                        type="text"
                                                        value={values.yearsInBusiness || ''}
                                                        onChange={(e) => {
                                                            // Only allow digits
                                                            const digitsOnly = e.target.value.replace(/\D/g, '');
                                                            // Limit to 4 digits (year format)
                                                            const limitedDigits = digitsOnly.slice(0, 4);
                                                            setFieldValue('yearsInBusiness', limitedDigits);
                                                        }}
                                                        className="mt-1 focus:ring-0 text-sm rounded-md block w-full border border-gray-300"
                                                        // placeholder="Starting year (YYYY)"
                                                        maxLength={4}
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1 mb-2">
                                                        Format (YYYY)
                                                    </p>

                                                    {values.yearsInBusiness && values.yearsInBusiness.length === 4 &&
                                                        parseInt(values.yearsInBusiness) >= 1900 &&
                                                        parseInt(values.yearsInBusiness) <= new Date().getFullYear() && (
                                                            <span className="text-sm text-blue-600 whitespace-nowrap font-medium">
                                                                ({new Date().getFullYear() - parseInt(values.yearsInBusiness)} years)
                                                            </span>
                                                        )}
                                                </div>
                                            </div>
                                            <div className="lg:col-span-2">
                                                <label className="block text-sm font-medium mb-1">
                                                    Company Type
                                                </label>
                                                <CustomSelect
                                                    options={[
                                                        { value: "Proprietorship", label: "Proprietorship" },
                                                        { value: "Partnership", label: "Partnership" },
                                                        { value: "LLP", label: "LLP" },
                                                        { value: "Private Limited", label: "Private Limited" },
                                                        { value: "Public Limited", label: "Public Limited" },
                                                        // { value: "Other", label: "Other" }
                                                    ]}
                                                    placeholder="Select Company Type"
                                                    selectedOption={values.companyType ? {
                                                        value: values.companyType,
                                                        label: values.companyType
                                                    } : null}
                                                    onChange={(option) => {
                                                        setFieldValue('companyType', option?.value || '');
                                                    }}
                                                    selectedId={values.companyType}
                                                    multipleSelection={false}
                                                    className="rounded-md bg-blue-500"
                                                />
                                                <ErrorMessage
                                                    name="companyType"
                                                    component="div"
                                                    className="mt-1 text-xs text-red-500"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 mt-2 gap-2">
                                            {/* Show GST field (always visible) */}
                                            <div>
                                                <label className="block text-sm font-medium">
                                                    GSTIN
                                                </label>
                                                <Field
                                                    type="text"
                                                    name="gstNumber"
                                                    id="gstNumber"
                                                    className="mt-1 px-1 focus:ring-0 text-sm rounded-md block w-full border border-gray-300"
                                                    onChange={(e) => {
                                                        const value = e.target.value.toUpperCase();
                                                        setFieldValue("gstNumber", value);
                                                        setFieldValue("panNumber", getPanFromGst(value));
                                                    }}
                                                />

                                                <ErrorMessage
                                                    name="gstNumber"
                                                    component="div"
                                                    className="mt-2 text-xs text-red-500"
                                                />
                                            </div>

                                            {/* Show PAN field only for contractor */}
                                            {/* {isContractorSelected(values.type) && ( */}
                                            <div className="col-span-1">
                                                <label className="block text-sm font-medium">
                                                    PAN Number<span className="text-red-500">*</span>
                                                </label>
                                                <Field
                                                    type="text"
                                                    name="panNumber"
                                                    id="panNumber"
                                                    maxLength={10}
                                                    aria-required="true"
                                                    className="mt-1 focus:ring-0 text-sm block w-full rounded-md border border-gray-300"
                                                />
                                                <ErrorMessage
                                                    name="panNumber"
                                                    component="div"
                                                    className="mt-2 text-xs text-red-500"
                                                />
                                            </div>
                                            {/* )} */}
                                        </div>
                                        </div>
                                    </section>

                                    <section className="rounded-2xl border border-[#E8E6DD] bg-white/95 p-5 shadow-sm">
                                    <h2 className="text-lg font-semibold pt-2">Contact Details</h2>
                                    <FieldArray name="contactDetails" id="contactDetails">
                                        {({ push, remove }) => (
                                            <>
                                                <p className="text-xs text-gray-500 mb-3">At least one contact detail is required</p>

                                                {/* Desktop Table (hidden on mobile) */}
                                                <div className="hidden md:block border border-gray-200 rounded-md overflow-hidden shadow-sm">
                                                    {/* Desktop Table Header */}
                                                    <div className="grid grid-cols-12 gap-2 bg-gray-50 p-2 border-b border-gray-200">
                                                        <div className="col-span-1">
                                                            <label className="block text-center text-xs font-medium text-gray-600">
                                                                Sr#
                                                            </label>
                                                        </div>
                                                        <div className="col-span-3">
                                                            <label className="block text-xs font-medium text-gray-600">
                                                                Full Name
                                                            </label>
                                                        </div>
                                                        <div className="col-span-2">
                                                            <label className="block text-xs font-medium text-gray-600">
                                                                WhatsApp number<span className="text-red-500">*</span>
                                                            </label>
                                                        </div>
                                                        <div className="col-span-4">
                                                            <label className="block text-xs font-medium text-gray-600">
                                                                Email<span className="text-red-500">*</span>
                                                            </label>
                                                        </div>
                                                        <div className="col-span-2">
                                                            <label className="block text-xs font-medium text-gray-600">
                                                                {/* Action */}
                                                            </label>
                                                        </div>
                                                    </div>

                                                    {/* Desktop Table Body */}
                                                    <div className="divide-y divide-gray-100">
                                                        {values.contactDetails.map((contact, index) => {
                                                            const emailFilled = !!contact.email;
                                                            const mobileFilled = !!contact.mobile;
                                                            const isEmailOrMobileMissing = !emailFilled && !mobileFilled;
                                                            const isFirstContact = index === 0;

                                                            return (
                                                                <div key={index} className="p-2 pb-3 hover:bg-gray-50/50 transition-colors duration-100">
                                                                    <div className="grid grid-cols-12 gap-2 items-center">
                                                                        {/* Serial Number */}
                                                                        <div className="col-span-1 flex items-center justify-center">
                                                                            <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full w-5 h-5 flex items-center justify-center">
                                                                                {index + 1}
                                                                            </span>
                                                                        </div>

                                                                        {/* Name Field */}
                                                                        <div className="col-span-3">
                                                                            <div className="relative">
                                                                                <Field
                                                                                    type="text"
                                                                                    name={`contactDetails[${index}].name`}
                                                                                    onChange={(e) => {
                                                                                        const lettersOnly = e.target.value.replace(/[^A-Za-z\s]/g, '');
                                                                                        setFieldValue(`contactDetails[${index}].name`, lettersOnly);
                                                                                    }}
                                                                                    className="block w-full focus:ring-0 text-sm rounded border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                                                />
                                                                            </div>
                                                                        </div>

                                                                        {/* Mobile Field */}
                                                                        <div className="col-span-2">
                                                                            <div className="relative">
                                                                                <Field
                                                                                    type="tel"
                                                                                    value={contact.mobile || ''}
                                                                                    onChange={(e) => handleMobileInput(e, setFieldValue, index)}
                                                                                    className="block w-full focus:ring-0 text-sm rounded border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                                                />
                                                                                <ErrorMessage
                                                                                    name={`contactDetails[${index}].mobile`}
                                                                                    component="div"
                                                                                    className="absolute -bottom-4 left-0 text-[10px] text-red-500"
                                                                                />
                                                                                {isEmailOrMobileMissing && (
                                                                                    <div className="absolute -bottom-4 left-0 text-[10px] text-red-500">Required</div>
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        {/* Email Field */}
                                                                        <div className="col-span-4">
                                                                            <div className="relative">
                                                                                <Field
                                                                                    type="email"
                                                                                    name={`contactDetails[${index}].email`}
                                                                                    className="block w-full focus:ring-0 text-sm rounded border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                                                />
                                                                                <ErrorMessage
                                                                                    name={`contactDetails[${index}].email`}
                                                                                    component="div"
                                                                                    className="absolute -bottom-4 left-0 text-[10px] text-red-500"
                                                                                />
                                                                                {isEmailOrMobileMissing && (
                                                                                    <div className="absolute -bottom-4 left-0 text-[10px] text-red-500">Required</div>
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        {/* Action Buttons */}
                                                                        <div className="col-span-2 flex items-center justify-center">
                                                                            <div className="flex items-center space-x-4">
                                                                                {/* Plus button to add new contact (only on last item) */}
                                                                                {index === values.contactDetails.length - 1 && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className={`text-xs flex items-center justify-center rounded ${values.contactDetails.every(
                                                                                            (c) => c.name && (c.email || c.mobile)
                                                                                        )
                                                                                            ? "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                                                                                            : "text-gray-300 cursor-not-allowed"
                                                                                            } transition-colors duration-150`}
                                                                                        onClick={() => push({ name: "", mobile: "", email: "" })}
                                                                                        disabled={
                                                                                            !values.contactDetails.every(
                                                                                                (c) => c.name && (c.email || c.mobile)
                                                                                            )
                                                                                        }
                                                                                        title="Add another contact"
                                                                                    >
                                                                                        {/* <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                                                                    </svg> */}
                                                                                        Add
                                                                                    </button>
                                                                                )}

                                                                                {/* Minus button to remove contact (not on first item) */}
                                                                                {!isFirstContact && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="text-xs flex items-center justify-center rounded text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                                                                                        onClick={() => remove(index)}
                                                                                        title="Remove this contact"
                                                                                    >
                                                                                        {/* <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                                                                                    </svg> */}
                                                                                        Remove
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Mobile Layout (shown on mobile, hidden on desktop) */}
                                                <div className="md:hidden space-y-4">
                                                    {values.contactDetails.map((contact, index) => {
                                                        const emailFilled = !!contact.email;
                                                        const mobileFilled = !!contact.mobile;
                                                        const isEmailOrMobileMissing = !emailFilled && !mobileFilled;
                                                        const isFirstContact = index === 0;

                                                        return (
                                                            <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                                                                <div className="flex justify-between items-center mb-3">
                                                                    <div className="flex items-center">
                                                                        <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-full w-6 h-6 flex items-center justify-center mr-2">
                                                                            {index + 1}
                                                                        </span>
                                                                        <h3 className="text-sm font-medium text-gray-700">Contact {index + 1}</h3>
                                                                    </div>

                                                                    {/* Remove button for mobile */}
                                                                    {!isFirstContact && (
                                                                        <button
                                                                            type="button"
                                                                            className="text-red-500 hover:text-red-700 p-1"
                                                                            onClick={() => remove(index)}
                                                                            title="Remove this contact"
                                                                        >
                                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                                            </svg>
                                                                        </button>
                                                                    )}
                                                                </div>

                                                                <div className="space-y-4">
                                                                    {/* Name Field */}
                                                                    <div>
                                                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                                                            Full Name
                                                                        </label>
                                                                        <Field
                                                                            type="text"
                                                                            name={`contactDetails[${index}].name`}
                                                                            onChange={(e) => {
                                                                                const lettersOnly = e.target.value.replace(/[^A-Za-z\s]/g, '');
                                                                                setFieldValue(`contactDetails[${index}].name`, lettersOnly);
                                                                            }}
                                                                            className="block w-full focus:ring-0 text-sm rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                                        />
                                                                    </div>

                                                                    {/* Mobile Field */}
                                                                    <div>
                                                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                                                            WhatsApp number<span className="text-red-500">*</span>
                                                                        </label>
                                                                        <div className="relative">
                                                                            <Field
                                                                                type="tel"
                                                                                value={contact.mobile || ''}
                                                                                onChange={(e) => handleMobileInput(e, setFieldValue, index)}
                                                                                className="block w-full focus:ring-0 text-sm rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                                            />
                                                                            <ErrorMessage
                                                                                name={`contactDetails[${index}].mobile`}
                                                                                component="div"
                                                                                className="mt-1 text-xs text-red-500"
                                                                            />
                                                                            {isEmailOrMobileMissing && (
                                                                                <div className="mt-1 text-xs text-red-500">Either email or mobile is required</div>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {/* Email Field */}
                                                                    <div>
                                                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                                                            Email<span className="text-red-500">*</span>
                                                                        </label>
                                                                        <div className="relative">
                                                                            <Field
                                                                                type="email"
                                                                                name={`contactDetails[${index}].email`}
                                                                                className="block w-full focus:ring-0 text-sm rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                                            />
                                                                            <ErrorMessage
                                                                                name={`contactDetails[${index}].email`}
                                                                                component="div"
                                                                                className="mt-1 text-xs text-red-500"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}

                                                    {/* Add Contact Button for Mobile */}
                                                    <div className="flex justify-center">
                                                        <button
                                                            type="button"
                                                            className={`flex items-center justify-center px-4 py-2 rounded ${values.contactDetails.every(
                                                                (c) => c.name && (c.email || c.mobile)
                                                            )
                                                                ? "bg-blue-600 text-white hover:bg-blue-700"
                                                                : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                                } transition-colors duration-150`}
                                                            onClick={() => push({ name: "", mobile: "", email: "" })}
                                                            disabled={
                                                                !values.contactDetails.every(
                                                                    (c) => c.name && (c.email || c.mobile)
                                                                )
                                                            }
                                                        >
                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                                            </svg>
                                                            Add Another Contact
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Footer - Show on both mobile and desktop */}
                                                <div className="mt-4 flex justify-between items-center">
                                                    <div className="text-xs text-gray-400">
                                                        {values.contactDetails.length} contact{values.contactDetails.length !== 1 ? 's' : ''}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        At least one of email or mobile is required
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </FieldArray>

                                    </section>

                                    <section className="rounded-2xl border border-[#E8E6DD] bg-white/95 p-5 shadow-sm">
                                    {/* vendor type */}
                                    <div>
                                        {/* <label className="block text-sm font-medium mt-4 mb-2">
                                            Vendor Type<span className="text-red-500">*</span>
                                        </label> */}
                                        <h2 className="text-lg font-semibold mt-4 mb-2">Vendor Type<span className="text-red-500">*</span></h2>

                                        <div className="text-sm">
                                            <div className="mb-3">
                                                <VendorMakeSelections
                                                    selections={materialMakeSelections(buildVendorTypeHierarchy(values, taxonomyAll), taxonomyAll)}
                                                    onChange={(subCategoryId, makeIds) => setFieldValue("makeIdsBySubcategory", { ...values.makeIdsBySubcategory, [subCategoryId]: makeIds }, false)}
                                                    onRemove={(removed) => {
                                                        const next = { ...values.makeIdsBySubcategory };
                                                        removed.forEach((id) => { next[id] = []; });
                                                        setFieldValue("makeIdsBySubcategory", next, false);
                                                    }}
                                                >
                                                {(renderMakes) => <VendorTaxonomySearch
                                                    renderSubCategoryDetails={(type, discipline, category, subCategory, options) => renderMakes(subCategory._id, options)}
                                                    hasSelections={hasVendorTypeSelections}
                                                    hideTaxonomyUntilVendorTypeSelected
                                                    onClearAll={clearVendorTypeSelections}
                                                    taxonomyAll={taxonomyAll}
                                                    searchTerm={categorySearchTerm}
                                                    onSearchTermChange={setCategorySearchTerm}
                                                    showTree
                                                    highlightedCategoryRefKey={highlightedCategoryRefKey}
                                                    isResultSelected={(result) =>
                                                        isTaxonomySearchResultSelected(result, values)
                                                    }
                                                    onResultSelect={(result, checked) =>
                                                        applyTaxonomySearchResult({
                                                            checked,
                                                            result,
                                                            setFieldValue,
                                                            values,
                                                        })
                                                    }
                                                    isVendorTypeSelected={(type) =>
                                                        (values.type || []).includes(type)
                                                    }
                                                    onVendorTypeToggle={(type, checked) => {
                                                        const selectedTypes = values.type || [];

                                                        if (checked) {
                                                            setFieldValue("type", [...selectedTypes, type]);
                                                            return;
                                                        }

                                                        setFieldValue(
                                                            "type",
                                                            selectedTypes.filter((selectedType) => selectedType !== type)
                                                        );
                                                        setFieldValue(`subtype_${type}`, []);
                                                        setFieldValue(
                                                            "categories",
                                                            (values.categories || []).filter(
                                                                (categoryKey) => !categoryKey.startsWith(`${type} - `)
                                                            )
                                                        );
                                                        setFieldValue(
                                                            "subcategories",
                                                            (values.subcategories || []).filter(
                                                                (subcategoryKey) => !subcategoryKey.startsWith(`${type} - `)
                                                            )
                                                        );
                                                    }}
                                                    isDisciplineSelected={(type, discipline) =>
                                                        (values[`subtype_${type}`] || []).includes(discipline?.name)
                                                    }
                                                    onDisciplineToggle={(type, discipline, checked) => {
                                                        const subtypeField = `subtype_${type}`;
                                                        const selected = values[subtypeField] || [];
                                                        const updated = checked
                                                            ? [...selected, discipline?.name]
                                                            : selected.filter((discName) => discName !== discipline?.name);

                                                        setFieldValue(subtypeField, updated);
                                                    }}
                                                    isCategorySelected={(type, discipline, category) =>
                                                        (values.categories || []).includes(
                                                            getCategoryKey(type, discipline?.name, category?.name)
                                                        )
                                                    }
                                                    onCategoryToggle={(type, discipline, category, checked) =>
                                                        toggleCategorySelection({
                                                            category,
                                                            categoryKey: getCategoryKey(type, discipline?.name, category?.name),
                                                            checked,
                                                            disciplineName: discipline?.name,
                                                            setFieldValue,
                                                            type,
                                                            values,
                                                        })
                                                    }
                                                    getCategoryError={(type, discipline, category) => {
                                                        const categoryKey = getCategoryKey(
                                                            type,
                                                            discipline?.name,
                                                            category?.name,
                                                        );
                                                        const shouldShowError =
                                                            Boolean(touched.subcategories || touched.categories) &&
                                                            Boolean(errors.subcategories) &&
                                                            (values.categories || []).includes(categoryKey) &&
                                                            isCategoryMissingRequiredSubcategory(
                                                                categoryKey,
                                                                values.subcategories || [],
                                                                taxonomyAll,
                                                            );

                                                        return shouldShowError
                                                            ? "Please select at least one subcategory."
                                                            : "";
                                                    }}
                                                    isSubCategorySelected={(type, discipline, category, subCategory) =>
                                                        (values.subcategories || []).includes(
                                                            getSubcategoryKey(
                                                                type,
                                                                discipline?.name,
                                                                category?.name,
                                                                subCategory?.name,
                                                            )
                                                        )
                                                    }
                                                    onSubCategoryToggle={(type, discipline, category, subCategory, checked) => {
                                                        const subcategoryKey = getSubcategoryKey(
                                                            type,
                                                            discipline?.name,
                                                            category?.name,
                                                            subCategory?.name,
                                                        );

                                                        const selectedSubcategories = values.subcategories || [];
                                                        setFieldValue(
                                                            "subcategories",
                                                            checked
                                                                ? [...selectedSubcategories, subcategoryKey]
                                                                : selectedSubcategories.filter((item) => item !== subcategoryKey)
                                                        );
                                                    }}
                                                    getCategoryRefKey={getCategoryRefKey}
                                                    onCategoryRef={({ node, refKey }) => {
                                                        if (node && refKey) {
                                                            categoryRefs.current[refKey] = node;
                                                        }
                                                    }}
                                                />}
                                                </VendorMakeSelections>
                                            </div>

                                            {vendorTypeValidation && (
                                                <div
                                                    id={vendorTypeValidation.id}
                                                    className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-600"
                                                >
                                                    {vendorTypeValidation.message}
                                                </div>
                                            )}

                                        </div>

                                        {/* CATEGORY + SUBCATEGORY PANELS */}
                                        {false && (
                                            <div className="mt-4 space-y-6">

                                            {Object.keys(taxonomyAll || {}).map((type) => {
                                                const subtypeField = `subtype_${type}`;
                                                const mainTax = taxonomyAll[type];
                                                const selectedSubtypes = values[subtypeField] || [];

                                                if (!values.type.includes(type) || selectedSubtypes.length === 0 || !mainTax)
                                                    return null;

                                                return (
                                                    <div key={type} className="p-4 rounded-lg border shadow-md">

                                                        {selectedSubtypes.map((disc, index) => {
                                                            const d = mainTax.disciplines.find((x) => x.name === disc);
                                                            if (!d) return null;

                                                            return (
                                                                <div
                                                                    key={disc}
                                                                    className={`${index > 0 ? "pt-6 mt-6 border-t border-gray-300" : ""}`}
                                                                >
                                                                    <h4 className="text-sm font-medium mb-3 text-gray-800">
                                                                        {type} — {disc}
                                                                    </h4>

                                                                    {/* CATEGORIES + SUBCATEGORIES */}
                                                                    {d.categories?.length > 0 ? (
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                                            {[...d.categories]
                                                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                                                .map((c, index) => {
                                                                                    const categoryKey = getCategoryKey(type, disc, c.name);
                                                                                    const isCategorySelected = values.categories?.includes(categoryKey);

                                                                                    return (
                                                                                        <div
                                                                                            key={c.name}
                                                                                            ref={(node) => {
                                                                                                if (node) {
                                                                                                    categoryRefs.current[
                                                                                                        getCategoryRefKey(type, disc, c.name)
                                                                                                    ] = node;
                                                                                                }
                                                                                            }}
                                                                                            className={`rounded-md border p-2 transition-all ${isCategorySelected
                                                                                                ? "border-blue-300 bg-blue-50/40 shadow-sm"
                                                                                                : "border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/20"
                                                                                                }`}
                                                                                        >
                                                                                            <button
                                                                                                type="button"
                                                                                                className="flex w-full items-start justify-between gap-2 text-left"
                                                                                                onClick={() =>
                                                                                                    toggleCategorySelection({
                                                                                                        category: c,
                                                                                                        categoryKey,
                                                                                                        disciplineName: disc,
                                                                                                        setFieldValue,
                                                                                                        type,
                                                                                                        values,
                                                                                                    })
                                                                                                }
                                                                                            >
                                                                                                <span className="text-sm font-medium text-gray-800">
                                                                                                    {index + 1}. {c.name}
                                                                                                </span>
                                                                                                <span
                                                                                                    className={`mt-[-2px] text-gray-500 transition-transform duration-200 ${isCategorySelected ? "rotate-180 text-blue-600" : ""
                                                                                                        }`}
                                                                                                />
                                                                                            </button>

                                                                                            {isCategorySelected && (
                                                                                                c.subCategories?.length > 0 ? (
                                                                                                    <div className="mt-2 border-l border-blue-200 pl-3">
                                                                                                        <div className="flex flex-col gap-1.5">
                                                                                                            {[...c.subCategories]
                                                                                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                                                                                .map((sc) => (
                                                                                                                    <label
                                                                                                                        key={sc.name}
                                                                                                                        className="inline-flex items-center"
                                                                                                                    >
                                                                                                                        <Field
                                                                                                                            type="checkbox"
                                                                                                                            name="subcategories"
                                                                                                                            value={getSubcategoryKey(type, disc, c.name, sc.name)}
                                                                                                                            className="w-4 h-4 rounded"
                                                                                                                            checked={values.subcategories?.includes(getSubcategoryKey(type, disc, c.name, sc.name))}
                                                                                                                        />
                                                                                                                        <span className="ml-2 text-xs text-gray-700">
                                                                                                                            {sc.name}
                                                                                                                        </span>
                                                                                                                    </label>
                                                                                                                ))}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <div className="mt-2 rounded-md border border-dashed border-blue-200 bg-white/70 px-3 py-2 text-xs text-gray-600">
                                                                                                        No subcategories available for this category.
                                                                                                    </div>
                                                                                                )
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
                                                                            No categories available for this discipline.
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })}
                                            {(
                                                (!values.type?.length && errors.type) ||
                                                (
                                                    values.type?.length > 0 &&
                                                    !values.type.includes("Others") &&
                                                    !(values.categories || []).length &&
                                                    errors.categories
                                                ) ||
                                                errors.subcategories
                                            ) && (
                                                    <div className="mt-2 text-xs text-red-500">
                                                    {errors.subcategories || "Please select a vendor type and category."}
                                                </div>
                                            )}
                                            </div>
                                        )}


                                        {/* OTHERS PANEL */}
                                        {values?.type?.includes("Others") && (
                                            <div className="mt-4 lg:w-1/2">
                                                <label className="block text-sm font-medium">
                                                    Material / Service<span className="text-red-500">*</span>
                                                </label>
                                                <Field
                                                    type="text"
                                                    name="otherVendorType"
                                                    className="mt-1 text-sm focus:ring-0 rounded-md block w-full border border-gray-300"
                                                />
                                                <ErrorMessage
                                                    name="otherVendorType"
                                                    component="div"
                                                    className="mt-1 text-xs text-red-500"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    </section>

                                   
                                    {/* make/Brands field - Only show for Material_Supplier */}
                                {/* <h2 className="text-lg font-semibold pt-2">Business Capabilities</h2>
                                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2 mt-4 ">
                                    {isMaterialSupplierSelected(values.type) && (
                                        <div className="mt-3 md:mt-4 lg:col-span-2">
                                            <label className="block text-sm font-medium">
                                                Make/Brands
                                            </label>
                                            <p className="text-xs text-gray-500 mt-1 mb-2">
                                                List of brand names you work with
                                            </p>
                                            <FieldArray name="brands">
                                                {({ push, remove }) => (
                                                    <div className="space-y-2 text-sm">
                                                        <div className="relative">
                                                            <div
                                                                className={`flex flex-wrap min-h-[38px] rounded-md text-sm gap-1 md:gap-2 px-2 py-1 w-full border ${errors.brands && touched.brands ? 'border-red-300' : 'border-gray-300'} bg-white transition-colors duration-200 focus-within:border-blue-500 focus:ring-0 focus-within:ring-blue-500`}
                                                            >
                                                                {values.brands && Array.isArray(values.brands) && values.brands.map((brand, index) => (
                                                                    <div
                                                                        key={index}
                                                                        className="group flex items-center gap-1 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full transition-colors duration-150"
                                                                    >
                                                                        <span className="max-w-[100px] md:max-w-[150px] truncate">{brand}</span>
                                                                        <button
                                                                            type="button"
                                                                            className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 focus:outline-none focus:ring-blue-300 transition-colors duration-150"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                remove(index);
                                                                            }}
                                                                            aria-label={`Remove ${brand}`}
                                                                        >
                                                                            <svg
                                                                                className="w-2 h-2 md:w-3 md:h-3"
                                                                                fill="none"
                                                                                stroke="currentColor"
                                                                                viewBox="0 0 24 24"
                                                                            >
                                                                                <path
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                    strokeWidth="2"
                                                                                    d="M6 18L18 6M6 6l12 12"
                                                                                />
                                                                            </svg>
                                                                        </button>
                                                                    </div>
                                                                ))}

                                                                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                                                                    <input
                                                                        id="brandInput"
                                                                        type="text"
                                                                        className="flex-1 focus:ring-0 min-w-[120px] border-0 bg-transparent px-1 py-1 text-sm focus:outline-none placeholder:text-xs placeholder-gray-400"
                                                                        placeholder={values.brands?.length > 0 ? "Add another brand..." : "Enter brand name & press enter or comma to add"}
                                                                        onKeyDown={(e) => {
                                                                            if ((e.key === 'Enter' || e.key === ',') && e.target.value.trim()) {
                                                                                e.preventDefault();
                                                                                const value = e.target.value.trim().replace(/,$/, '');
                                                                                if (value && !values.brands?.includes(value)) {
                                                                                    push(value);
                                                                                    e.target.value = '';
                                                                                }
                                                                            }
                                                                        }}
                                                                        onBlur={(e) => {
                                                                            if (e.target.value.trim()) {
                                                                                const value = e.target.value.trim().replace(/,$/, '');
                                                                                if (value && !values.brands?.includes(value)) {
                                                                                    push(value);
                                                                                    e.target.value = '';
                                                                                }
                                                                            }
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="rounded-md border px-3 py-1 text-xs hover:bg-gray-200"
                                                                        onMouseDown={(e) => e.preventDefault()}
                                                                        onClick={() => {
                                                                            const input = document.getElementById("brandInput");
                                                                            const value = input?.value?.trim().replace(/,$/, '');

                                                                            if (!value) return;

                                                                            if (!values.brands?.includes(value)) {
                                                                                push(value);
                                                                            }

                                                                            input.value = '';
                                                                            input.focus();
                                                                        }}
                                                                    >
                                                                        Add
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            <div className="absolute -bottom-5 left-0">
                                                                <ErrorMessage
                                                                    name="brands"
                                                                    component="div"
                                                                    className="text-xs text-red-500"
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center justify-between text-xs text-gray-500">
                                                            {values.brands && values.brands.length > 0 && (
                                                                <span>{values.brands.length} brand(s) added</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </FieldArray>

                                        </div>
                                    )}
                                </div> */}
                                {/* Resources - Contractor Only */}
                                 {(isMaterialSupplierSelected(values.type) ||
                                        isContractorSelected(values.type) ||
                                        editVendorDetails?.servicesOffering?.length > 0) && (
                                    <section className="rounded-2xl border border-[#E8E6DD] bg-white/95 p-5 shadow-sm">
                                {isContractorSelected(values.type) && (
                                    <>
                                        {/* <hr className="my-4" /> */}
                                        <h2 className="text-lg font-semibold mb-3">Resources</h2>

                                        <div className="grid grid-cols-2 lg:grid-cols-12 gap-4 w-full items-start">
                                            {/* Machinaries Owned */}
                                            <div className="col-span-2 lg:col-span-9">
                                                <label className="block text-sm font-medium pb-1">
                                                    Machinaries/Equipments Owned
                                                </label>

                                                <FieldArray name="machinaries">
                                                    {({ push, remove }) => (
                                                        <div className="space-y-2 text-sm min-h-[70px]">
                                                            <div
                                                                className={`flex flex-wrap content-start lg:h-[104px] min-h-[70px] lg:overflow-y-auto rounded-md text-sm gap-1 md:gap-2 px-2 py-1 w-full border ${errors.machinaries && touched.machinaries
                                                                        ? "border-red-300"
                                                                        : "border-gray-300"
                                                                    } bg-white transition-colors duration-200 focus-within:border-blue-500`}
                                                            >
                                                                {values.machinaries?.map((machine, index) => (
                                                                    <div
                                                                        key={index}
                                                                        className="shrink-0 flex items-center gap-1 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 text-xs font-medium px-2 rounded-full transition-colors"
                                                                    >
                                                                        <span className="leading-none">{machine}</span>

                                                                        <button
                                                                            type="button"
                                                                            onClick={() => remove(index)}
                                                                            className="flex items-center justify-center leading-none hover:text-red-500"
                                                                        >
                                                                            ✕
                                                                        </button>
                                                                    </div>
                                                                ))}

                                                                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                                                                    <input
                                                                        id="machineryInput"
                                                                        type="text"
                                                                        className="flex-1 border-0 bg-transparent px-1 py-1 text-sm focus:outline-none focus:ring-0 placeholder-gray-400 placeholder:text-xs"
                                                                        placeholder={values.machinaries?.length > 0 ? "Add another machine..." : "Enter machinery or equipment"}
                                                                        onKeyDown={(e) => {
                                                                            if (
                                                                                (e.key === "Enter" || e.key === ",") &&
                                                                                e.target.value.trim()
                                                                            ) {
                                                                                e.preventDefault();
                                                                                const value = e.target.value.trim();

                                                                                if (
                                                                                    value &&
                                                                                    !values.machinaries?.includes(value)
                                                                                ) {
                                                                                    push(value);
                                                                                }

                                                                                e.target.value = "";
                                                                            }
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="rounded-md border px-3 py-1 text-xs hover:bg-gray-200"
                                                                        onClick={() => {
                                                                            const input = document.getElementById("machineryInput");
                                                                            const value = input?.value?.trim();

                                                                            if (!value) return;

                                                                            if (!values.machinaries?.includes(value)) {
                                                                                push(value);
                                                                            }

                                                                            input.value = "";
                                                                            input.focus();
                                                                        }}
                                                                    >
                                                                        Add
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </FieldArray>
                                            </div>
                                            <div className="col-span-2 lg:col-span-3 flex flex-col justify-between h-full">
                                                {/* Manpower */}
                                                <div className="grid grid-cols-2 gap-4 lg:gap-0 lg:grid-cols-1">
                                                    <label className="block text-sm font-medium pb-1">
                                                        Manpower
                                                    </label>

                                                    <Field name="manpower">
                                                        {({ field, form }) => (
                                                            <input
                                                                {...field}
                                                                type="text"
                                                                className="block w-full focus:ring-0 rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-xs"
                                                                placeholder="Total manpower available"
                                                                onChange={(e) =>
                                                                    handleDigitsOnlyInput(
                                                                        e,
                                                                        form.setFieldValue,
                                                                        "manpower"
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </Field>
                                                </div>
                                                {/* Staff */}
                                                <div>
                                                    <label className="block text-sm font-medium pb-1">
                                                        Staff
                                                    </label>

                                                    <Field name="staff">
                                                        {({ field, form }) => (
                                                            <input
                                                                {...field}
                                                                type="text"
                                                                className="block w-full rounded-md focus:ring-0 border border-gray-300 px-3 py-2 text-sm placeholder:text-xs"
                                                                placeholder="Total staff available"
                                                                onChange={(e) =>
                                                                    handleDigitsOnlyInput(
                                                                        e,
                                                                        form.setFieldValue,
                                                                        "staff"
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </Field>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {editVendorDetails?.servicesOffering?.length > 0 && (
                                    <>
                                        <div className="mb-4 w-full">
                                            <label className="block text-sm font-medium mb-2"> Services </label>
                                            <div className="flex flex-wrap gap-2">
                                                {editVendorDetails?.servicesOffering?.map((service) => (
                                                    <span key={service?._id} className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full" >
                                                        {service?.serviceName}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}

                                    </section>
                                    )}

                                    <section className="rounded-2xl border border-[#E8E6DD] bg-white/95 p-5 shadow-sm">
                                {/* address */}
                                <h2 className="text-base font-semibold pt-2">Address</h2>
                                {isEdit && isUnchangedLegacyAddress(values, editVendorDetails?.location) && (
                                    <p className="mb-2 text-xs text-gray-500">
                                        You can keep this saved address while updating other details. If you change the address, complete the pincode, area, city and state.
                                    </p>
                                )}
                                <PincodeAddressFields preserveSavedAddress={Boolean(isEdit)} />
                                    </section>

                                    <section className="rounded-2xl border border-[#E8E6DD] bg-white/95 p-5 shadow-sm">
                                <h2 className="text-lg font-semibold pt-2">Client Names & Documents</h2>
                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                                    <div className="mt-3 md:mt-4">
                                        <label className="block text-sm font-medium ">
                                            Client Names
                                        </label>
                                        <p className="text-xs text-gray-500 mt-1 mb-2">
                                            List of clients worked with
                                        </p>

                                        <FieldArray name="clientNames">
                                            {({ push, remove }) => (
                                                <div className="space-y-2 text-sm">
                                                    <div className="relative">
                                                        <div
                                                            className={`flex flex-wrap rounded-md text-sm gap-1 md:gap-2 p-2 w-full border ${errors.clientNames && touched.clientNames ? 'border-red-300' : 'border-gray-300'} bg-white transition-colors duration-200 focus-within:border-blue-500 focus:ring-0 focus-within:ring-blue-500`}
                                                        >
                                                            {values.clientNames && Array.isArray(values.clientNames) && values.clientNames.map((client, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="group flex items-center gap-1 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 text-xs md:text-sm font-medium px-2 py-1 rounded-full transition-colors duration-150"
                                                                >
                                                                    <span className="max-w-[100px] md:max-w-[150px] truncate">{client}</span>
                                                                    <button
                                                                        type="button"
                                                                        className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 focus:outline-none focus:ring-blue-300 transition-colors duration-150"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            remove(index);
                                                                        }}
                                                                        aria-label={`Remove ${client}`}
                                                                    >
                                                                        <svg
                                                                            className="w-2 h-2 md:w-3 md:h-3"
                                                                            fill="none"
                                                                            stroke="currentColor"
                                                                            viewBox="0 0 24 24"
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                strokeWidth="2"
                                                                                d="M6 18L18 6M6 6l12 12"
                                                                            />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            ))}

                                                            <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                                                                <input
                                                                    id="clientNameInput"
                                                                    type="text"
                                                                    className="flex-1 focus:ring-0 border-0 bg-transparent px-1 py-1 text-sm focus:outline-none placeholder-gray-400 placeholder:text-xs"
                                                                    placeholder={
                                                                        values.clientNames?.length > 0
                                                                            ? "Add another client..."
                                                                            : "Enter client name & press Enter or comma to add"
                                                                    }
                                                                    onKeyDown={(e) => {
                                                                        if ((e.key === "Enter" || e.key === ",") && e.target.value.trim()) {
                                                                            e.preventDefault();

                                                                            const value = e.target.value.trim().replace(/,$/, "");

                                                                            if (
                                                                                value &&
                                                                                !values.clientNames?.some(
                                                                                    (client) =>
                                                                                        client.toLowerCase() === value.toLowerCase()
                                                                                )
                                                                            ) {
                                                                                push(value);
                                                                            }

                                                                            e.target.value = "";
                                                                        }
                                                                    }}
                                                                />

                                                                <button
                                                                    type="button"
                                                                    className="rounded-md px-3 py-1 text-xs border hover:bg-gray-200"
                                                                    onClick={() => {
                                                                        const input = document.getElementById("clientNameInput");

                                                                        if (!input?.value?.trim()) return;

                                                                        const value = input.value.trim();

                                                                        if (
                                                                            !values.clientNames?.some(
                                                                                (client) =>
                                                                                    client.toLowerCase() === value.toLowerCase()
                                                                            )
                                                                        ) {
                                                                            push(value);
                                                                        }

                                                                        input.value = "";
                                                                        input.focus();
                                                                    }}
                                                                >
                                                                    Add
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div className="absolute -bottom-5 left-0">
                                                            <ErrorMessage
                                                                name="clientNames"
                                                                component="div"
                                                                className="text-xs text-red-500"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center justify-between text-xs text-gray-500 pb-10">
                                                        {/* <span>Press Enter or comma to add</span> */}
                                                        {values.clientNames && values.clientNames.length > 0 && (
                                                            <span>{values.clientNames.length} client(s) added</span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </FieldArray>
                                    </div>

                                    <div className="mt-3 md:mt-4">
                                        <label className="block text-sm font-medium">
                                            Documents
                                        </label>
                                        <p className="text-xs text-gray-500 mt-1 mb-2">
                                            Upload PDF, Word, Excel, CSV, TXT, JPG or PNG files only.
                                        </p>

                                        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-2">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <input
                                                    id="vendorDocumentsInput"
                                                    type="file"
                                                    multiple
                                                    accept={vendorDocumentAccept}
                                                    className="hidden"
                                                    onChange={(event) => handleVendorDocumentUpload(event, values, setFieldValue)}
                                                    disabled={documentsUploading}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => document.getElementById("vendorDocumentsInput")?.click()}
                                                    disabled={documentsUploading}
                                                    className={`rounded-md border px-3 py-1.5 text-xs font-medium ${documentsUploading
                                                        ? "cursor-wait border-gray-300 bg-gray-200 text-gray-500"
                                                        : "border-blue-300 bg-white text-blue-700 hover:bg-blue-50"
                                                        }`}
                                                >
                                                    {documentsUploading ? "Uploading..." : "Upload Documents"}
                                                </button>
                                                <span className="text-xs text-gray-500">
                                                    Standard document/image types only
                                                </span>
                                            </div>

                                            {values.attachments?.length > 0 && (
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {values.attachments.map((doc, index) => (
                                                        <span
                                                            key={`${doc.Name}-${index}`}
                                                            className="flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-white px-2 py-1 text-xs"
                                                        >
                                                            <span className="max-w-[220px] truncate" title={doc.Name}>
                                                                {doc.Name}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="font-semibold text-red-600 hover:text-red-800"
                                                                onClick={() => removeVendorDocument(index, values, setFieldValue)}
                                                            >
                                                                x
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                    </section>
                                </div>

                                {/* Sticky Footer */}
                                {(!isEdit || publicUpdateToken) &&
                                    <div className="sticky bottom-0 z-50 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-md backdrop-blur sm:px-6 lg:px-8" style={{
                                        boxShadow: "0px -1px 14px 0px rgba(0, 0, 0, 0.1)",
                                    }}>
                                        <div className="flex justify-end items-center gap-3">
                                            {/* Submit Button */}
                                            <button
                                                type="button"
                                                onClick={() => setShowSubmitConfirmPopup(true)} // Show confirmation popup
                                                className={`rounded-sm px-5 py-2 text-sm ${loading
                                                    ? "bg-blue-400 cursor-not-allowed"
                                                    : "bg-[#1F1E29] hover:bg-black"
                                                    } text-white font-medium`}
                                                disabled={loading || documentsUploading}
                                            >
                                                {loading ? (
                                                    <span className="flex items-center">
                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                        </svg>
                                                        {/* {isEdit ? "Updating..." : isExternalRegistration ? "Submitting..." : "Creating..."} */}
                                                    </span>
                                                ) : documentsUploading ? (
                                                    "Uploading documents..."
                                                ) : publicUpdateToken ? (
                                                    "Submit Updated Profile"
                                                ) : isEdit ? (
                                                    "Update Vendor"
                                                ) : true ? (
                                                    "Submit"
                                                ) : (
                                                    "Submit Form"
                                                )}
                                            </button>

                                            {/* Delete Button (only for edit mode) */}
                                            {isEdit && !publicUpdateToken && (
                                                <button
                                                    type="button"
                                                    onClick={handleOpenDeletePopup}
                                                    className="px-4 py-2 border border-red-400 bg-red-500 text-white hover:bg-red-600 rounded"
                                                >
                                                    Delete Vendor
                                                </button>
                                            )}
                                        </div>

                                        {errorMessage != "" && (
                                            <div className="mt-2 text-center">
                                                <p className="text-red-500">{errorMessage}</p>
                                            </div>
                                        )}
                                    </div>}

                                {/* Submit Confirmation Popup */}
                                {showSubmitConfirmPopup && (
                                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                                        <div className="bg-white rounded-lg p-6 max-w-md w-full">
                                            <div className="flex justify-between items-center mb-4">
                                                <h2 className="text-xl font-bold">Confirm Submission</h2>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowSubmitConfirmPopup(false)}
                                                    className="text-gray-500 hover:text-gray-700"
                                                >
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>

                                            <div className="mb-6">
                                                <p className="text-gray-700">
                                                    Are you sure you want to submit the form?
                                                </p>
                                                <p className="text-sm text-gray-500 mt-2">
                                                    Please ensure all information is correct before submitting.
                                                </p>
                                            </div>

                                            <div className="flex justify-end space-x-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowSubmitConfirmPopup(false)}
                                                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 font-medium"
                                                >
                                                    No, Go Back
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        if (documentsUploading) {
                                                            enqueueSnackbar("Please wait until document upload completes", { variant: "warning" });
                                                            return;
                                                        }

                                                        setShowSubmitConfirmPopup(false);

                                                        // Validate form first
                                                        const validationErrors = await validateForm();

                                                        if (Object.keys(validationErrors).length === 0) {
                                                            setLoading(true);
                                                            try {
                                                                await submitForm();
                                                            } catch (error) {
                                                                console.error("Submission error:", error);
                                                            } finally {
                                                                setLoading(false);
                                                            }
                                                        } else {
                                                            setTouched(Object.keys(values).reduce((acc, key) => {
                                                                acc[key] = true;
                                                                return acc;
                                                            }, {}));

                                                            // Scroll to first error
                                                            const firstErrorField = Object.keys(validationErrors)[0];
                                                            const errorElement = document.querySelector(`[name="${firstErrorField}"]`) ||
                                                                document.getElementById(firstErrorField);
                                                            if (errorElement) {
                                                                errorElement.scrollIntoView({
                                                                    behavior: "smooth",
                                                                    block: "center",
                                                                });
                                                                errorElement.focus();
                                                            }
                                                        }
                                                    }}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                                                >
                                                    Yes, Submit Form
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                            </Form>
                        </>
                    )
                }}
            </Formik>


        </div>
    );
};

export default VendorInvitationForm;
